/**
 * Author: @seetam-cultsport
 * Custom toast notification system
 * Provides a global toast notification functionality with customizable status and message
 */

/**
 * CsToastProvider custom element
 * Manages the creation and display of toast notifications
 */
class CsToastProvider extends HTMLElement {
  constructor() {
    super();
    this.toastContainer = null;
    this.baseReadingTime = 1000; // Base time in ms
    this.wordsPerMinute = 250; // Average reading speed
  }

  /**
   * Initialize toast container when element is connected
   */
  connectedCallback() {
    this.toastContainer = this.querySelector("cs-toast-container");
  }

  /**
   * Calculate display duration based on message length
   * @param {string} message - Message to display
   * @returns {number} Duration in milliseconds
   */
  calculateDisplayDuration(message) {
    const words = message.trim().split(/\s+/).length;
    const readingTimeMs = (words / this.wordsPerMinute) * 60 * 1000;
    return Math.max(this.baseReadingTime, Math.min(readingTimeMs + 2000, 8000));
  }

  /**
   * Display a new toast notification
   * @param {string} status - Status type of toast (success, error, warning, info)
   * @param {string} message - Message to display in toast
   */
  async showToast(status, message) {
    // Clone toast template
    const toastItemTemplate = this.toastContainer
      .querySelector("cs-toast-item")
      .cloneNode(true);

    const messageIcon = toastItemTemplate.querySelector(
      `.toast-item__message-icon--${status}`,
    );

    const messageEl = toastItemTemplate.querySelector(
      ".toast-item__message-text",
    );
    const progressBar = document.createElement("div");
    progressBar.classList.add("toast-item__progress");

    // Set up toast content
    messageEl.innerHTML = message;
    toastItemTemplate.classList.add("active", status);
    messageIcon.classList.remove("hidden");
    toastItemTemplate.appendChild(progressBar);

    this.toastContainer.appendChild(toastItemTemplate);

    // Calculate display duration based on message length
    const displayDuration = this.calculateDisplayDuration(message);

    // Ensure progress bar is rendered correctly
    await new Promise((resolve) => setTimeout(resolve, 0));
    await new Promise((resolve) =>
      requestAnimationFrame(() => {
        progressBar.style.transform = "scaleX(0)";
        progressBar.style.transition = `transform ${displayDuration}ms linear`;
        resolve();
      }),
    );

    // Add close button handler
    const closeBtn = toastItemTemplate.querySelector(".toast-item__close-btn");
    closeBtn.addEventListener("click", () => {
      this.hideToast(toastItemTemplate);
    });

    // Auto-hide after calculated duration
    await new Promise((resolve) => setTimeout(resolve, displayDuration));
    this.hideToast(toastItemTemplate);
  }

  /**
   * Hide and remove a toast notification
   * @param {HTMLElement} toastItem - Toast element to remove
   */
  hideToast(toastItem) {
    toastItem.classList.add("removing");
    toastItem.addEventListener("animationend", () => {
      toastItem.remove();
    });
  }
}

// Register custom element
customElements.define("cs-toast-provider", CsToastProvider);

/**
 * Global toast function
 * Allows showing toasts from anywhere in the application
 * @param {string} status - Status type of toast
 * @param {string} message - Message to display
 */
window.csToast = {
  showToast(status, message) {
    const toastProvider = document.querySelector("cs-toast-provider");
    if (toastProvider) {
      toastProvider.showToast(status, message);
    }
  },
};

/*
Usage Examples:

1. Show success toast:
csToast.showToast('success', 'Item added to cart successfully!');

2. Show error toast:
csToast.showToast('error', 'Something went wrong!');

3. Show warning toast:
csToast.showToast('warning', 'Please review your input');

4. Show info toast:
csToast.showToast('info', 'New features available');

Note: Make sure you have the required HTML structure in your document:
<cs-toast-provider>
  <cs-toast-container>
    <cs-toast-item>
      <div class="toast-item__message-text"></div>
      <button class="toast-item__close-btn"></button>
    </cs-toast-item>
  </cs-toast-container>
</cs-toast-provider>

And the corresponding CSS (cs-toast.css) is loaded.
*/
